import { createSlice } from '@reduxjs/toolkit';

import { status } from 'utils/const';
import { thunks as profileThunks } from 'store/profile/thunks';
import { thunks } from './thunks';
import { selectors } from './selectors';

const STUDENT_MUTATIONS = [
  'createDancer/submitDancerCreation/fulfilled',
  'dancerDetails/updateDancerInfo/fulfilled',
  'dancerDetails/deleteDancer/fulfilled',
  'dancers/importDancers/fulfilled',
  'createParent/createRelative/fulfilled',
  'parentDetails/createRelative/fulfilled',
];

const TEACHER_MUTATIONS = [
  'parents/createMember/fulfilled',
  'parents/editEmployees/fulfilled',
  'parents/deleteTeamMember/fulfilled',
  'parents/removeEmployeesAvatar/fulfilled',
  'profile/updateUserInfo/fulfilled',
];

const ROOM_MUTATIONS = [
  'configuration/updateConfigurationSettings/fulfilled',
  'configuration/deleteLocation/fulfilled',
  'configuration/deleteRoom/fulfilled',
];

const PARENT_MUTATIONS = [
  'createParent/submitParentCreation/fulfilled',
  'parentDetails/updateParentInfo/fulfilled',
  'parentDetails/deleteParent/fulfilled',
  'createDancer/createGuardian/fulfilled',
  'dancerDetails/createGuardian/fulfilled',
];

const initialState = {
  profile: null,
  studio: null,
  isSidebarOpen: false,
  recipientsList: [],
  teachers: [],
  teachersByRoutine: [],
  rooms: [],
  states: [],
  cities: [],
  timezones: [],
  users: [],
  parents: [],
  classes: [],
  countries: [],
  search: null,
  students: [],
  statesLoaded: false,
  roomsLoaded: false,
  studentsLoaded: false,
  teachersLoaded: false,
  teachersRequestId: null,
  usersLoaded: false,
  parentsLoaded: false,
  classesLoaded: false,
  tags: [],
  nicknames: [],
  tagsLoaded: false,
  nicknamesLoaded: false,

  routineConsts: {
    statuses: [],
    types: [],
    levels: [],
    styles: [],
  },
  fetchingStatesStatus: status.IDLE,
  fetchingStudioStatus: status.IDLE,
  fetchingCitiesStatus: status.IDLE,
  fetchingTimezonesStatus: status.IDLE,
  fetchingSearchStatus: status.IDLE,
  fetchingTagsSearchStatus: status.IDLE,
};

const slice = createSlice({
  name: 'general',
  initialState,
  reducers: {
    setSidebarOpen: (state, { payload }) => {
      state.isSidebarOpen = payload;
    },
    clearSearch: (state) => {
      state.search = null;
    },
    setStudentsLoaded: (state, { payload }) => {
      state.studentsLoaded = payload;
    },
    setTeachersLoaded: (state, { payload }) => {
      state.teachersLoaded = payload;
    },
    setRoomsLoaded: (state, { payload }) => {
      state.roomsLoaded = payload;
    },
    setParentsLoaded: (state, { payload }) => {
      state.parentsLoaded = payload;
    },
    setUsersLoaded: (state, { payload }) => {
      state.usersLoaded = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(thunks.getStates.pending, (state) => {
        state.fetchingStatesStatus = status.PENDING;
      })
      .addCase(thunks.getStates.fulfilled, (state, { payload }) => {
        state.states = payload.map(({ id, name }) => ({
          value: id,
          label: name,
        }));
        state.fetchingStatesStatus = status.SUCCESS;
        state.statesLoaded = true;
      })
      .addCase(thunks.getStates.rejected, (state) => {
        state.fetchingStatesStatus = status.FAIL;
      })

      .addCase(thunks.getCities.pending, (state) => {
        state.fetchingCitiesStatus = status.PENDING;
      })
      .addCase(thunks.getCities.fulfilled, (state, { payload }) => {
        state.cities = payload.map(({ id, city }) => ({
          value: id,
          label: city,
        }));
        state.fetchingCitiesStatus = status.SUCCESS;
      })
      .addCase(thunks.getCities.rejected, (state) => {
        state.fetchingCitiesStatus = status.FAIL;
      })

      .addCase(thunks.getTimezones.pending, (state) => {
        state.fetchingTimezonesStatus = status.PENDING;
      })
      .addCase(thunks.getTimezones.fulfilled, (state, { payload }) => {
        state.timezones = payload.data.map(({ id, name }) => ({
          value: id,
          label: name,
        }));
        state.fetchingTimezonesStatus = status.SUCCESS;
      })
      .addCase(thunks.getTimezones.rejected, (state) => {
        state.fetchingTimezonesStatus = status.FAIL;
      })

      .addCase(thunks.getRooms.fulfilled, (state, { payload }) => {
        state.rooms = payload.data;
        state.roomsLoaded = true;
      })

      .addCase(thunks.getTeachers.pending, (state, action) => {
        state.teachersRequestId = action.meta.requestId;
      })
      .addCase(thunks.getTeachers.fulfilled, (state, action) => {
        if (state.teachersRequestId !== action.meta.requestId) return;

        state.teachers = action.payload.data;
        state.teachersLoaded = true;
      })

      .addCase(thunks.getTeachersByRoutine.fulfilled, (state, { payload }) => {
        state.teachersByRoutine = payload.data.sort(
          (a, b) => b.is_active - a.is_active
        );
      })

      .addCase(thunks.getStudents.fulfilled, (state, { payload }) => {
        state.students = payload.data;
        state.studentsLoaded = true;
      })

      .addCase(thunks.getUsers.fulfilled, (state, action) => {
        const { data } = action.payload;

        state.users = data;
        state.usersLoaded = true;
      })

      .addCase(thunks.getParents.fulfilled, (state, { payload }) => {
        state.parents = payload.data;
        state.parentsLoaded = true;
      })

      .addCase(thunks.getClasses.fulfilled, (state, { payload }) => {
        state.classes = payload.data;
        state.classesLoaded = true;
      })

      .addCase(thunks.search.pending, (state) => {
        state.fetchingSearchStatus = status.PENDING;
      })
      .addCase(thunks.search.fulfilled, (state, { payload }) => {
        state.search = payload.data;
        state.fetchingSearchStatus = status.SUCCESS;
      })
      .addCase(thunks.search.rejected, (state) => {
        state.fetchingSearchStatus = status.FAIL;
      })

      .addCase(thunks.getProfile.fulfilled, (state, { payload }) => {
        state.profile = payload.data;
      })

      .addCase(profileThunks.updateUserInfo.fulfilled, (state, { payload }) => {
        if (!state.profile || !payload?.data) return;

        const { avatar, firstName, lastName, name, fullName, id } =
          payload.data;
        const userId = state.profile.userId || id;

        if (avatar !== undefined) {
          state.profile.avatar = avatar || null;
        }

        const nextName =
          name || fullName || [firstName, lastName].filter(Boolean).join(' ');

        if (nextName) {
          state.profile.name = nextName;
          if (state.profile.fullName !== undefined) {
            state.profile.fullName = nextName;
          }
        }

        const patchTeacher = (teacher) => {
          const isCurrentUser =
            teacher.user?.id === userId || teacher.user_id === userId;

          if (!isCurrentUser) return;

          if (avatar !== undefined) {
            teacher.avatar = avatar || null;
            if (teacher.user) teacher.user.avatar = avatar || null;
          }

          if (!nextName) return;

          teacher.full_name = nextName;
          if (firstName) teacher.first_name = firstName;
          if (lastName) teacher.last_name = lastName;

          if (!teacher.user) return;

          teacher.user.fullName = nextName;
          if (firstName) teacher.user.firstName = firstName;
          if (lastName) teacher.user.lastName = lastName;
        };

        state.teachers.forEach(patchTeacher);
        state.teachersByRoutine.forEach(patchTeacher);
      })

      .addCase(thunks.getStudio.pending, (state) => {
        state.fetchingStudioStatus = status.PENDING;
      })
      .addCase(thunks.getStudio.fulfilled, (state, { payload }) => {
        state.studio = payload.data;
        state.fetchingStudioStatus = status.SUCCESS;
      })
      .addCase(thunks.getStudio.rejected, (state) => {
        state.fetchingStudioStatus = status.FAIL;
      })
      .addCase(thunks.updateStudio.fulfilled, (state, { payload }) => {
        state.studio = {
          ...state.studio,
          ...payload.data,
          addressLineFirst: payload.data.address_line_first,
          addressLineSecond: payload.data.address_line_second,
        };
      })
      .addCase(thunks.updateStudio.rejected, (state) => {
        state.fetchingStudioStatus = status.FAIL;
      })

      .addCase(thunks.getRoutineTypes.fulfilled, (state, { payload }) => {
        state.routineConsts.types = payload.data?.map((t) => ({
          value: t,
          label: t,
        }));
      })

      .addCase(thunks.getRoutineStatuses.fulfilled, (state, { payload }) => {
        state.routineConsts.statuses = payload.data?.map((s) => ({
          value: s,
          label: s,
        }));
      })

      .addCase(thunks.getRoutineLevels.fulfilled, (state, { payload }) => {
        state.routineConsts.levels = payload.data?.map((l) => ({
          value: l,
          label: l,
        }));
      })

      .addCase(thunks.getStyles.fulfilled, (state, { payload }) => {
        state.styles = payload.data?.map((s) => ({
          value: s.id,
          label: s.name,
        }));
      })

      .addCase(thunks.getRecipientsList.fulfilled, (state, { payload }) => {
        const preparedRecipients = payload.data.map((r) => ({
          ...r,
          value: r.userId,
          label: r.fullName,
        }));

        state.recipientsList = preparedRecipients;
      })

      .addCase(thunks.getTags.pending, (state) => {
        state.fetchingTagsSearchStatus = status.PENDING;
      })
      .addCase(thunks.getTags.fulfilled, (state, { payload }) => {
        state.tags = payload.data;
        state.fetchingTagsSearchStatus = status.SUCCESS;
        state.tagsLoaded = true;
      })
      .addCase(thunks.getTags.rejected, (state) => {
        state.fetchingTagsSearchStatus = status.FAIL;
      })

      .addCase(thunks.getNicknames.fulfilled, (state, { payload }) => {
        state.nicknames = payload.data;
        state.nicknamesLoaded = true;
      })

      .addMatcher(
        (action) => STUDENT_MUTATIONS.includes(action.type),
        (state) => {
          state.studentsLoaded = false;
        }
      )
      .addMatcher(
        (action) => TEACHER_MUTATIONS.includes(action.type),
        (state) => {
          state.teachersLoaded = false;
          state.teachersRequestId = null;
          state.usersLoaded = false;
        }
      )
      .addMatcher(
        (action) => ROOM_MUTATIONS.includes(action.type),
        (state) => {
          state.roomsLoaded = false;
        }
      )
      .addMatcher(
        (action) => PARENT_MUTATIONS.includes(action.type),
        (state) => {
          state.parentsLoaded = false;
        }
      );
  },
});

const general = {
  actions: slice.actions,
  thunks,
  selectors,
};

export { general };
export default slice.reducer;
